import { useState, useEffect, useMemo, useRef } from "react";
import Api from "../../../../shared/api/api";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FaFacebookF,
  FaInstagram,
  FaPinterestP,
  FaRedditAlien,
  FaYoutube,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import "../styles/AdminBlogs.css";
import "../styles/AdminBooks.css";
import "../styles/AdminEmailTemplates.css";
import "../styles/AdminAutoSocialPosts.css";

// Emoji categories for content editor
const emojiCategories = {
  classic: ["😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣", "🙂", "😉"],
  expressive: [
    "😊",
    "😇",
    "😍",
    "🤩",
    "😘",
    "😎",
    "🤔",
    "😐",
    "😶",
    "🙄",
    "😏",
    "🥺",
  ],
  dramatic: ["😤", "😠", "😡", "😢", "😭", "😱", "😨", "😓", "😥"],
  celebration: ["🔥", "✨", "💫", "⚡", "🎉", "🎊", "🏆", "🌟", "🌙", "⭐"],
  writing: ["📚", "✍️", "📝", "✒️", "🎧", "🕰️", "🧭", "🧩", "🔍"],
  fantasy: ["🐉", "🗡️", "🛡️", "🔮", "🧙‍♂️", "🧝‍♂️", "🧛‍♂️", "👁️"],
  utility: ["📸", "🎬", "🎵", "💡", "💬", "📣", "👀", "🌄"],
};

const postTypeOptions = ["Merch", "Book", "Blog", "Engagement"];

const platformOptions = [
  {
    code: "fb",
    name: "Facebook",
    color: "#1877f2",
    Icon: FaFacebookF,
  },
  {
    code: "ig",
    name: "Instagram",
    color: "#c13584",
    Icon: FaInstagram,
  },
  {
    code: "x",
    name: "X / Twitter",
    color: "#000000",
    Icon: FaXTwitter,
  },
  {
    code: "pt",
    name: "Pinterest",
    color: "#e60023",
    Icon: FaPinterestP,
  },
  {
    code: "rd",
    name: "Reddit",
    color: "#ff4500",
    Icon: FaRedditAlien,
  },
  {
    code: "yt",
    name: "YouTube",
    color: "#ff0000",
    Icon: FaYoutube,
  },
];

const X_CHAR_LIMIT = 25000;

export default function AdminAutoSocialPosts() {
  const [posts, setPosts] = useState([]);
  const [todayPlannedData, setTodayPlannedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plannedLoading, setPlannedLoading] = useState(true);

  // Filters
  const [filterType, setFilterType] = useState("all");
  const [filterActive, setFilterActive] = useState("all");
  const [filterPosted, setFilterPosted] = useState("all");
  const [filterPlatform, setFilterPlatform] = useState("all");

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPost, setPreviewPost] = useState(null);

  // Form state
  const [postType, setPostType] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [contentHtml, setContentHtml] = useState("");
  const [xContent, setXContent] = useState("");
  const [imgUrl, setImgUrl] = useState("");
  const [firstComment, setFirstComment] = useState("");
  const [dbType, setDbType] = useState("");
  const [platforms, setPlatforms] = useState("");
  const [canPost, setCanPost] = useState(true);
  const [prioritized, setPrioritized] = useState(false);
  const [active, setActive] = useState(true);
  const [posted, setPosted] = useState(false);
  const [notes, setNotes] = useState("");
  const [priorityWeight, setPriorityWeight] = useState("");

  // Emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("classic");
  const quillRef = useRef(null);

  // Ratio state
  const [engagementRatio, setEngagementRatio] = useState("");
  const [otherRatio, setOtherRatio] = useState("");
  const [ratiosLoaded, setRatiosLoaded] = useState(false);

  useEffect(() => {
    loadPosts();
    loadTodayPlannedPost();
    loadRatioSettings();
  }, []);

  const normalizeAutoPostDateValue = (value) => {
    if (!value) return value;

    const raw = String(value);

    /*
      MySQL DATETIME may arrive as:
      - 2026-07-08 13:56:00
      - 2026-07-08T13:56:00.000Z

      We store scheduled_at as the intended Pacific wall-clock time.
      So we strip it down to the literal date/time pieces and DO NOT let
      JavaScript convert it to browser local time.
    */
    const match = raw.match(
      /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/
    );

    if (!match) return raw;

    const [, year, month, day, hour, minute, second = "00"] = match;

    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  };

  const normalizeAutoPostRows = (rows) => {
    if (!Array.isArray(rows)) return [];

    return rows.map((post) => ({
      ...post,
      scheduled_at: normalizeAutoPostDateValue(post.scheduled_at),
      posted_at: normalizeAutoPostDateValue(post.posted_at),
      processing_started_at: normalizeAutoPostDateValue(post.processing_started_at),
      created_at: normalizeAutoPostDateValue(post.created_at),
      updated_at: normalizeAutoPostDateValue(post.updated_at),
    }));
  };

  const loadPosts = async () => {
    setLoading(true);
    try {
      const data = await Api.getAllAutoPosts();
      setPosts(normalizeAutoPostRows(Array.isArray(data) ? data : []));
    } catch (err) {
      console.error("Error loading auto posts:", err);
      setPosts([]);
    }
    setLoading(false);
  };

  const loadTodayPlannedPost = async () => {
    setPlannedLoading(true);
    try {
      const data = await Api.getTodayPlannedAutoPost();

      const normalizedPosts = normalizeAutoPostRows(
        Array.isArray(data?.posts) ? data.posts : []
      );

      setTodayPlannedData({
        ...(data || {}),
        posts: normalizedPosts,
        plannedPost: data?.plannedPost
          ? normalizeAutoPostRows([data.plannedPost])[0]
          : null,
      });
    } catch (err) {
      console.error("Error loading today's planned auto post:", err);
      setTodayPlannedData(null);
    }
    setPlannedLoading(false);
  };

  const reloadAllPostData = async () => {
    await Promise.all([loadPosts(), loadTodayPlannedPost()]);
  };

  const loadRatioSettings = async () => {
    try {
      const res = await Api.getSettings();
      const s = res?.settings || {};

      if (s.auto_post_engagement_ratio !== undefined) {
        setEngagementRatio(s.auto_post_engagement_ratio);
      }

      if (s.auto_post_other_ratio !== undefined) {
        setOtherRatio(s.auto_post_other_ratio);
      }

      setRatiosLoaded(true);
    } catch (err) {
      console.error("Error loading settings:", err);
    }
  };

  const saveRatioSettings = async () => {
    try {
      await Api.updateSettings({
        auto_post_engagement_ratio: Number(engagementRatio),
        auto_post_other_ratio: Number(otherRatio),
      });

      alert("Ratio saved!");
    } catch (err) {
      console.error("Error saving ratio:", err);
      alert("Failed to save ratio.");
    }
  };

  const resetForm = () => {
    setPostType("");
    setTitle("");
    setContent("");
    setContentHtml("");
    setXContent("");
    setImgUrl("");
    setFirstComment("");
    setDbType("");
    setPlatforms("");
    setCanPost(true);
    setPrioritized(false);
    setActive(true);
    setPosted(false);
    setNotes("");
    setPriorityWeight("");
    setShowEmojiPicker(false);
    setEmojiSearch("");
    setActiveCategory("classic");
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setCurrentId(null);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = async (post) => {
    try {
      const full = await Api.getAutoPost(post.id);
      const normalizedFull = normalizeAutoPostRows([full])[0];

      setIsEditMode(true);
      setCurrentId(normalizedFull.id);

      const baseContent = normalizedFull.content || "";

      const baseHtml =
        baseContent.length > 0
          ? baseContent
              .split("\n")
              .map((line) =>
                line === ""
                  ? "<p><br/></p>"
                  : `<p>${line
                      .replace(/&/g, "&amp;")
                      .replace(/</g, "&lt;")
                      .replace(/>/g, "&gt;")}</p>`
              )
              .join("")
          : "";

      setPostType(normalizedFull.post_type || "");
      setTitle(normalizedFull.title || "");
      setContent(baseContent);
      setContentHtml(baseHtml);
      setXContent(normalizedFull.x_content || "");
      setImgUrl(normalizedFull.img_url || "");
      setFirstComment(normalizedFull.first_comment || "");
      setDbType(normalizedFull.db_type || "");
      setPlatforms(normalizedFull.platforms || "");
      setCanPost(normalizedFull.can_post === 1 || normalizedFull.can_post === true);
      setPrioritized(
        normalizedFull.prioritized === 1 || normalizedFull.prioritized === true
      );
      setActive(normalizedFull.active === 1 || normalizedFull.active === true);
      setPosted(normalizedFull.posted === 1 || normalizedFull.posted === true);
      setNotes(normalizedFull.notes || "");
      setPriorityWeight(normalizedFull.priority_weight ?? "");
      setShowEmojiPicker(false);
      setEmojiSearch("");
      setActiveCategory("classic");
      setShowModal(true);
    } catch (err) {
      console.error("Failed to load full auto post:", err);
      alert("Failed to load full auto post.");
    }
  };

  const openPreview = (post) => {
    setPreviewPost(post);
    setShowPreviewModal(true);
  };

  const closePreview = () => {
    setPreviewPost(null);
    setShowPreviewModal(false);
  };

  const handleContentChange = (value, delta, source, editor) => {
    setContentHtml(value || "");
    setContent(editor.getText() || "");
  };

  const getFilteredEmojis = () => {
    const list = emojiCategories[activeCategory] || [];
    if (!emojiSearch.trim()) return list;

    const q = emojiSearch.toLowerCase();
    return list.filter((em) => em.toLowerCase().includes(q));
  };

  const insertEmoji = (emoji) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();

    editor.insertText(index, emoji);
    editor.setSelection(index + emoji.length);

    setContentHtml(editor.root.innerHTML || "");
    setContent(editor.getText() || "");
  };

  const getSelectedPlatforms = () =>
    String(platforms || "")
      .split(",")
      .map((p) => p.trim().toLowerCase())
      .filter(Boolean);

  const isPlatformSelected = (code) => {
    return getSelectedPlatforms().includes(code);
  };

  const togglePlatform = (code) => {
    const selected = getSelectedPlatforms();

    let next;
    if (selected.includes(code)) {
      next = selected.filter((p) => p !== code);

      if (code === "x") {
        setXContent("");
      }
    } else {
      next = [...selected, code];
    }

    setPlatforms(next.join(","));
  };

  const savePost = async () => {
    if (!postType.trim()) {
      return alert("Post type is required.");
    }

    if (!title.trim()) {
      return alert("Title is required.");
    }

    if (!content.trim()) {
      return alert("Main platform content is required.");
    }

    const selectedPlatformCodes = getSelectedPlatforms();
    const selectedX = selectedPlatformCodes.includes("x");
    const nonXPlatforms = selectedPlatformCodes.filter((p) => p !== "x");
    const effectiveXContent = (xContent.trim() || content.trim()).trim();

    if (!selectedPlatformCodes.length) {
      return alert("At least one platform is required.");
    }

    if (selectedX && !effectiveXContent) {
      return alert("X/Twitter content is required when X is selected.");
    }

    if (selectedX && effectiveXContent.length > X_CHAR_LIMIT) {
      return alert(
        `X/Twitter content is ${effectiveXContent.length} characters. Keep it at ${X_CHAR_LIMIT} or less.`
      );
    }

    const basePayload = {
      post_type: postType,
      title,
      img_url: imgUrl,
      first_comment: firstComment,
      db_type: dbType,
      can_post: canPost ? 1 : 0,
      prioritized: prioritized ? 1 : 0,
      active: active ? 1 : 0,
      posted: posted ? 1 : 0,
      status: posted ? "posted" : "pending",
      notes,
      priority_weight:
        priorityWeight === "" ? null : Number(priorityWeight) || 1,
    };

    try {
      if (isEditMode && currentId != null) {
        const payload = {
          ...basePayload,
          content,
          x_content: selectedX ? effectiveXContent : "",
          platforms: selectedPlatformCodes.join(","),
        };

        await Api.updateAutoPost(currentId, payload);
      } else {
        let mainPostId = null;

        if (nonXPlatforms.length > 0) {
          const mainRes = await Api.createAutoPost({
            ...basePayload,
            content,
            x_content: "",
            platforms: nonXPlatforms.join(","),
            linked_post_id: null,
          });

          mainPostId = mainRes?.id || null;
        }

        if (selectedX) {
          await Api.createAutoPost({
            ...basePayload,
            title: `${title} - X`,
            content: effectiveXContent,
            x_content: effectiveXContent,
            first_comment: "",
            platforms: "x",
            linked_post_id: mainPostId,
          });
        }
      }

      setShowModal(false);
      setIsEditMode(false);
      setCurrentId(null);
      resetForm();
      await reloadAllPostData();
    } catch (err) {
      console.error("Save Error:", err);
      alert("Failed to save auto social post.");
    }
  };

  const handleToggleActive = async (post) => {
    try {
      await Api.toggleAutoPostActive(post.id);
      await reloadAllPostData();
    } catch (err) {
      console.error("Toggle active error:", err);
      alert("Failed to toggle active state.");
    }
  };

  const handleTogglePosted = async (post) => {
    try {
      await Api.toggleAutoPostPosted(post.id);
      await reloadAllPostData();
    } catch (err) {
      console.error("Toggle posted error:", err);
      alert("Failed to toggle posted state.");
    }
  };

  const handleResetPosted = async () => {
    if (!window.confirm("Reset 'posted' to 0 for ALL auto posts?")) return;

    try {
      await Api.resetAllAutoPostsPosted();
      await reloadAllPostData();
    } catch (err) {
      console.error("Reset posted error:", err);
      alert("Failed to reset posted flags.");
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete auto post "${post.title}"? This cannot be undone.`)) {
      return;
    }

    try {
      await Api.deleteAutoPost(post.id);
      await reloadAllPostData();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete auto post.");
    }
  };

  const copyContent = async (post) => {
    try {
      await navigator.clipboard.writeText(post.content || "");
      alert("Content copied to clipboard.");
    } catch (err) {
      console.error("Clipboard error:", err);
      alert("Failed to copy content.");
    }
  };

  const copyXContent = async (post) => {
    try {
      await navigator.clipboard.writeText(post.x_content || post.content || "");
      alert("X/Twitter content copied to clipboard.");
    } catch (err) {
      console.error("Clipboard error:", err);
      alert("Failed to copy X/Twitter content.");
    }
  };

  const renderPlatformBadges = (platformString) => {
    if (!platformString) return <span style={{ color: "#999" }}>None</span>;

    const platformList = String(platformString)
      .split(",")
      .map((p) => p.trim().toLowerCase())
      .filter(Boolean);

    const labelMap = {
      fb: "FB",
      ig: "IG",
      x: "X",
      pt: "PT",
      rd: "RD",
      yt: "YT",
    };

    const tooltipMap = {
      fb: "Facebook",
      ig: "Instagram",
      x: "X / Twitter",
      pt: "Pinterest",
      rd: "Reddit",
      yt: "YouTube",
    };

    const colorMap = {
      fb: "#1877f2",
      ig: "#c13584",
      x: "#000000",
      pt: "#e60023",
      rd: "#ff4500",
      yt: "#ff0000",
    };

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {platformList.map((code, idx) => {
          const label = labelMap[code] || code.toUpperCase();
          const tooltip = tooltipMap[code] || code.toUpperCase();
          const bg = colorMap[code] || "#666";

          return (
            <span
              key={code + idx}
              className="platformBadge"
              data-tooltip={tooltip}
              style={{
                background: bg,
                color: "#fff",
                padding: "3px 8px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "default",
              }}
            >
              {label}
            </span>
          );
        })}
      </div>
    );
  };

  const formatPacificDateTime = (value) => {
    if (!value) return "Not scheduled";

    const normalized = normalizeAutoPostDateValue(value);
    const raw = String(normalized);

    const match = raw.match(
      /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/
    );

    if (!match) return raw;

    const [, year, month, day, hourRaw, minute] = match;

    let hour = Number(hourRaw);
    const ampm = hour >= 12 ? "PM" : "AM";

    hour = hour % 12;
    if (hour === 0) hour = 12;

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const monthText = monthNames[Number(month) - 1] || month;

    return `${monthText} ${Number(day)}, ${year}, ${hour}:${minute} ${ampm} PDT`;
  };

  const getStatusClass = (post) => {
    const status = String(post?.status || "").toLowerCase();

    if (post?.posted || status === "posted") return "posted";
    if (status === "failed") return "failed";
    if (status === "scheduled") return "scheduled";

    return "pending";
  };

  const postTypes = useMemo(() => {
    const set = new Set();

    posts.forEach((p) => {
      if (p.post_type) set.add(p.post_type);
    });

    return Array.from(set);
  }, [posts]);

  const allPlatforms = useMemo(() => {
    const set = new Set();

    posts.forEach((p) => {
      if (!p.platforms) return;

      String(p.platforms)
        .split(",")
        .map((x) => x.trim().toLowerCase())
        .filter(Boolean)
        .forEach((code) => set.add(code));
    });

    return Array.from(set);
  }, [posts]);

  const filteredPosts = useMemo(() => {
    let list = [...posts];

    if (filterType !== "all") {
      list = list.filter((p) => (p.post_type || "") === filterType);
    }

    if (filterActive !== "all") {
      const isActive = filterActive === "active";
      list = list.filter((p) =>
        isActive ? p.active === 1 || p.active === true : !p.active
      );
    }

    if (filterPosted !== "all") {
      const isPosted = filterPosted === "posted";
      list = list.filter((p) =>
        isPosted ? p.posted === 1 || p.posted === true : !p.posted
      );
    }

    if (filterPlatform !== "all") {
      list = list.filter((p) => {
        if (!p.platforms) return false;

        const parts = String(p.platforms)
          .split(",")
          .map((x) => x.trim().toLowerCase());

        return parts.includes(filterPlatform);
      });
    }

    list.sort((a, b) => {
      const ta = (a.post_type || "").toLowerCase();
      const tb = (b.post_type || "").toLowerCase();

      if (ta < tb) return -1;
      if (ta > tb) return 1;

      return (a.id || 0) - (b.id || 0);
    });

    return list;
  }, [posts, filterType, filterActive, filterPosted, filterPlatform]);

  const groupedByType = useMemo(() => {
    const groups = {};

    filteredPosts.forEach((p) => {
      const key = p.post_type || "Uncategorized";
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });

    return groups;
  }, [filteredPosts]);

  const groupedTypeKeys = useMemo(
    () => Object.keys(groupedByType).sort((a, b) => a.localeCompare(b)),
    [groupedByType]
  );

  const plannedPosts = Array.isArray(todayPlannedData?.posts)
    ? todayPlannedData.posts
    : [];

  const closeModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setCurrentId(null);
    resetForm();
  };

  const selectedPlatforms = getSelectedPlatforms();
  const xSelected = selectedPlatforms.includes("x");
  const xEffectivePreview = (xContent.trim() || content.trim()).trim();
  const xContentLength = xEffectivePreview.length;
  const xContentOverLimit = xContentLength > X_CHAR_LIMIT;

  return (
    <>

      <div className="autoPostPageContainer">
        <h1 className="autoPostHeader">Auto Social Post Management</h1>

        <div className="autoPostActionRow">
          <button className="autoPostBtnAdd" onClick={openCreateModal}>
            + Add Auto Post
          </button>

          <button className="autoPostBtnReset" onClick={handleResetPosted}>
            Reset Posted Flags
          </button>

          {ratiosLoaded && (
            <div className="autoPostRatioControls">
              <span style={{ fontWeight: 600 }}>Engagement Ratio:</span>
              <span>Engagement</span>

              <input
                type="number"
                min={1}
                value={engagementRatio}
                onChange={(e) =>
                  setEngagementRatio(Math.max(1, Number(e.target.value) || 1))
                }
              />

              <span>per</span>
              <span>Other</span>

              <input
                type="number"
                min={1}
                value={otherRatio}
                onChange={(e) =>
                  setOtherRatio(Math.max(1, Number(e.target.value) || 1))
                }
              />

              <button className="autoPostBtnAdd" onClick={saveRatioSettings}>
                Save Ratio
              </button>
            </div>
          )}
        </div>

        <section className="plannedPostSection">
          <div className="plannedPostHeaderRow">
            <div>
              <h2>Today's Planned Auto Post</h2>
              <p>
                This shows the post scheduled for today and remains visible until
                midnight Pacific.
              </p>
            </div>

            <button
              type="button"
              className="plannedPostRefreshBtn"
              onClick={loadTodayPlannedPost}
            >
              Refresh
            </button>
          </div>

          {plannedLoading ? (
            <div className="plannedPostEmpty">Loading planned post...</div>
          ) : plannedPosts.length === 0 ? (
            <div className="plannedPostEmpty">
              No post is planned for today yet.
            </div>
          ) : (
            <div className="plannedPostGrid">
              {plannedPosts.map((post) => (
                <div key={post.id} className="plannedPostCard">
                  <div className="plannedPostCardTop">
                    <div>
                      <h3>{post.title}</h3>
                      <div className="plannedPostMeta">
                        ID {post.id} · {post.post_type || "Uncategorized"}
                      </div>
                    </div>

                    <span className={`plannedPostStatus ${getStatusClass(post)}`}>
                      {post.status || (post.posted ? "posted" : "pending")}
                    </span>
                  </div>

                  <div className="plannedPostDetails">
                    <div>
                      <strong>Scheduled:</strong>{" "}
                      {formatPacificDateTime(post.scheduled_at)}
                    </div>

                    {post.posted_at && (
                      <div>
                        <strong>Posted At:</strong>{" "}
                        {formatPacificDateTime(post.posted_at)}
                      </div>
                    )}

                    <div>
                      <strong>Platforms:</strong>{" "}
                      {renderPlatformBadges(post.platforms)}
                    </div>

                    <div>
                      <strong>Active:</strong>{" "}
                      <span className={post.active ? "greenText" : "redText"}>
                        {post.active ? "Yes" : "No"}
                      </span>
                    </div>

                    <div>
                      <strong>Posted:</strong>{" "}
                      <span className={post.posted ? "greenText" : "blueText"}>
                        {post.posted ? "Yes" : "No"}
                      </span>
                    </div>

                    {post.linked_post_id && (
                      <div>
                        <strong>Linked Main Post:</strong> {post.linked_post_id}
                      </div>
                    )}
                  </div>

                  <div className="plannedPostContent">
                    {post.content || "(No content)"}
                  </div>

                  {post.error_message && (
                    <div className="plannedPostError">
                      <strong>Error:</strong> {post.error_message}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="autoPostFilterRow">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Types</option>
            {postTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>

          <select
            value={filterPosted}
            onChange={(e) => setFilterPosted(e.target.value)}
          >
            <option value="all">All Posted States</option>
            <option value="posted">Posted Only</option>
            <option value="unposted">Unposted Only</option>
          </select>

          <select
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
          >
            <option value="all">All Platforms</option>
            {allPlatforms.map((p) => (
              <option key={p} value={p}>
                {p.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : groupedTypeKeys.length === 0 ? (
          <p>No auto social posts found.</p>
        ) : (
          groupedTypeKeys.map((type) => (
            <section key={type} style={{ marginBottom: "2rem" }}>
              <h2 className="autoPostSectionTitle">{type}</h2>

              <table className="adminTable">
                <thead>
                  <tr>
                    <th className="col-id" style={{ width: "50px" }}>
                      ID
                    </th>
                    <th className="col-title">Title</th>
                    <th className="col-content">Content Preview</th>
                    <th className="col-platforms">Platforms</th>
                    <th className="col-flags">Flags</th>
                    <th className="col-actions" style={{ width: "260px" }}>
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {groupedByType[type].map((post) => (
                    <tr key={post.id}>
                      <td className="col-id">{post.id}</td>

                      <td className="col-title">{post.title}</td>

                      <td className="col-content">
                        <div className="contentPreviewBox">
                          {post.contentHtml || post.content}
                        </div>
                      </td>

                      <td className="col-platforms">
                        {renderPlatformBadges(post.platforms)}
                      </td>

                      <td>
                        <div
                          className="col-flags"
                          style={{ fontSize: "0.85rem", lineHeight: 1.4 }}
                        >
                          <div>
                            Active:{" "}
                            <strong
                              style={{
                                color:
                                  post.active === 1 || post.active === true
                                    ? "green"
                                    : "red",
                              }}
                            >
                              {post.active ? "Yes" : "No"}
                            </strong>
                          </div>

                          <div>
                            Posted:{" "}
                            <strong
                              style={{
                                color:
                                  post.posted === 1 || post.posted === true
                                    ? "#b91c1c"
                                    : "#2563eb",
                              }}
                            >
                              {post.posted ? "Yes" : "No"}
                            </strong>
                          </div>

                          <div>
                            Prioritized:{" "}
                            <strong
                              style={{
                                color:
                                  post.prioritized === 1 ||
                                  post.prioritized === true
                                    ? "#d97706"
                                    : "#4b5563",
                              }}
                            >
                              {post.prioritized ? "Yes" : "No"}
                            </strong>
                          </div>

                          {post.status && <div>Status: {post.status}</div>}

                          {post.scheduled_at && (
                            <div>
                              Scheduled: {formatPacificDateTime(post.scheduled_at)}
                            </div>
                          )}

                          {post.priority_weight != null && (
                            <div>Weight: {post.priority_weight}</div>
                          )}
                        </div>
                      </td>

                      <td className="col-actions">
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            alignItems: "stretch",
                          }}
                        >
                          <button
                            className="templateEditBtn"
                            onClick={() => openPreview(post)}
                          >
                            Preview
                          </button>

                          <button
                            className="templateEditBtn"
                            onClick={() => copyContent(post)}
                          >
                            Copy Content
                          </button>

                          {post.x_content && (
                            <button
                              className="templateEditBtn"
                              onClick={() => copyXContent(post)}
                            >
                              Copy X Content
                            </button>
                          )}

                          <button
                            className="templateEditBtn"
                            onClick={() => openEditModal(post)}
                          >
                            Edit
                          </button>

                          <button
                            className={
                              post.active ? "templateDeleteBtn" : "templateEditBtn"
                            }
                            onClick={() => handleToggleActive(post)}
                          >
                            {post.active ? "Deactivate" : "Activate"}
                          </button>

                          <button
                            className={
                              post.posted ? "templateDeleteBtn" : "templateEditBtn"
                            }
                            onClick={() => handleTogglePosted(post)}
                          >
                            {post.posted ? "Mark Unposted" : "Mark Posted"}
                          </button>

                          <button
                            className="templateDeleteBtn"
                            onClick={() => handleDelete(post)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))
        )}

        {showModal && (
          <div className="modalOverlay">
            <div className="modalContent">
              <h2>
                {isEditMode ? "Edit Auto Social Post" : "Create Auto Social Post"}
              </h2>

              <div className="formSection">
                <label>Post Type</label>
                <select
                  value={postType}
                  onChange={(e) => setPostType(e.target.value)}
                >
                  <option value="">Select Post Type</option>
                  {postTypeOptions.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>

                <label>Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />

                <label>Main Platform Post</label>

                <div className="emojiPickerWrap">
                  <button
                    type="button"
                    className="emojiToggleBtn"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    😀 Emojis
                  </button>

                  {showEmojiPicker && (
                    <div className="emojiPickerContainer">
                      <input
                        type="text"
                        className="emojiSearchInput"
                        placeholder="Search emoji…"
                        value={emojiSearch}
                        onChange={(e) => setEmojiSearch(e.target.value)}
                      />

                      <div className="emojiCategoryTabs">
                        {Object.keys(emojiCategories).map((cat) => (
                          <button
                            key={cat}
                            type="button"
                            className={
                              activeCategory === cat
                                ? "emojiCategoryTab active"
                                : "emojiCategoryTab"
                            }
                            onClick={() => setActiveCategory(cat)}
                          >
                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                          </button>
                        ))}
                      </div>

                      <div className="emojiGrid">
                        {getFilteredEmojis().map((em, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="emojiBtn"
                            onClick={() => insertEmoji(em)}
                          >
                            {em}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <ReactQuill
                  ref={quillRef}
                  value={contentHtml}
                  onChange={handleContentChange}
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      ["bold", "italic", "underline"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ align: [] }],
                      ["clean"],
                    ],
                  }}
                  style={{ height: "260px", marginBottom: "20px" }}
                />

                <label>Platforms</label>
                <div className="platformSelectorGrid">
                  {platformOptions.map((platform) => {
                    const selected = isPlatformSelected(platform.code);
                    const Icon = platform.Icon;

                    return (
                      <button
                        key={platform.code}
                        type="button"
                        className={
                          selected
                            ? "platformSelectBtn selected"
                            : "platformSelectBtn"
                        }
                        style={{
                          "--platform-color": platform.color,
                        }}
                        onClick={() => togglePlatform(platform.code)}
                        aria-pressed={selected}
                        title={platform.name}
                      >
                        <Icon className="platformIcon" />
                        <span className="platformName">{platform.name}</span>
                      </button>
                    );
                  })}
                </div>

                <input type="hidden" value={platforms} readOnly />

                {xSelected && (
                  <div className="xPostPanel">
                    <div className="xPostHeaderRow">
                      <label>X / Twitter Post</label>

                      <span
                        className={
                          xContentOverLimit
                            ? "xCharCounter overLimit"
                            : "xCharCounter"
                        }
                      >
                        {xContentLength}/{X_CHAR_LIMIT}
                      </span>
                    </div>

                    <textarea
                      value={xContent}
                      onChange={(e) => setXContent(e.target.value)}
                      placeholder="Write the shorter X/Twitter version here. If left blank, the main post is used if it is within the limit."
                      className="xPostTextarea"
                      maxLength={X_CHAR_LIMIT + 40}
                    />
                  </div>
                )}

                <label>First Comment (e.g., URL comment)</label>
                <textarea
                  value={firstComment}
                  onChange={(e) => setFirstComment(e.target.value)}
                  style={{ minHeight: "80px" }}
                />

                <label>Image URL</label>
                <input
                  type="text"
                  value={imgUrl}
                  onChange={(e) => setImgUrl(e.target.value)}
                />

                <label>DB Type (optional)</label>
                <input
                  type="text"
                  value={dbType}
                  onChange={(e) => setDbType(e.target.value)}
                />

                <label>Notes (internal)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  style={{ minHeight: "80px" }}
                />

                <label>Priority Weight (optional numeric)</label>
                <input
                  type="number"
                  value={priorityWeight}
                  onChange={(e) => setPriorityWeight(e.target.value)}
                />

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "16px",
                    marginTop: "10px",
                  }}
                >
                  <label className="checkboxRow">
                    <input
                      type="checkbox"
                      checked={canPost}
                      onChange={(e) => setCanPost(e.target.checked)}
                    />
                    Can Post
                  </label>

                  <label className="checkboxRow">
                    <input
                      type="checkbox"
                      checked={prioritized}
                      onChange={(e) => setPrioritized(e.target.checked)}
                    />
                    Prioritized
                  </label>

                  <label className="checkboxRow">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={(e) => setActive(e.target.checked)}
                    />
                    Active
                  </label>

                  <label className="checkboxRow">
                    <input
                      type="checkbox"
                      checked={posted}
                      onChange={(e) => setPosted(e.target.checked)}
                    />
                    Posted
                  </label>
                </div>
              </div>

              <div className="formSection">
                <label>Plain Text Preview</label>
                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    border: "1px solid #ddd",
                    padding: "8px",
                    borderRadius: "6px",
                    background: "#fafafa",
                    fontSize: "0.95rem",
                  }}
                >
                  {content || "(No content yet)"}
                </div>

                {xSelected && (
                  <>
                    <label style={{ marginTop: "14px" }}>X / Twitter Preview</label>
                    <div
                      style={{
                        maxHeight: "160px",
                        overflowY: "auto",
                        whiteSpace: "pre-wrap",
                        border: xContentOverLimit
                          ? "1px solid #b91c1c"
                          : "1px solid #ddd",
                        padding: "8px",
                        borderRadius: "6px",
                        background: "#fafafa",
                        fontSize: "0.95rem",
                      }}
                    >
                      {xEffectivePreview || "(No X/Twitter content yet)"}
                    </div>
                  </>
                )}
              </div>

              <div className="buttonRow">
                <button className="publishingSubmitBtn" onClick={savePost}>
                  Save Auto Post
                </button>

                <button className="cancelBtn" onClick={closeModal}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showPreviewModal && previewPost && (
          <div className="modalOverlay">
            <div className="modalContent">
              <h2>Auto Post Preview (ID {previewPost.id})</h2>

              <p>
                <strong>Title:</strong> {previewPost.title}
              </p>

              <p>
                <strong>Type:</strong> {previewPost.post_type}
              </p>

              <p>
                <strong>Platforms:</strong>{" "}
                {renderPlatformBadges(previewPost.platforms)}
              </p>

              {previewPost.scheduled_at && (
                <p>
                  <strong>Scheduled:</strong>{" "}
                  {formatPacificDateTime(previewPost.scheduled_at)}
                </p>
              )}

              {previewPost.status && (
                <p>
                  <strong>Status:</strong> {previewPost.status}
                </p>
              )}

              <label>Content</label>
              <div
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                  border: "1px solid #ddd",
                  padding: "8px",
                  borderRadius: "6px",
                  background: "#fafafa",
                  fontSize: "0.95rem",
                  marginBottom: "1rem",
                }}
              >
                {previewPost.content}
              </div>

              {previewPost.x_content && (
                <>
                  <label>X / Twitter Content</label>
                  <div
                    style={{
                      maxHeight: "180px",
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                      border: "1px solid #ddd",
                      padding: "8px",
                      borderRadius: "6px",
                      background: "#fafafa",
                      fontSize: "0.9rem",
                      marginBottom: "1rem",
                    }}
                  >
                    {previewPost.x_content}
                  </div>
                </>
              )}

              {previewPost.first_comment && (
                <>
                  <label>First Comment</label>
                  <div
                    style={{
                      maxHeight: "150px",
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                      border: "1px solid #ddd",
                      padding: "8px",
                      borderRadius: "6px",
                      background: "#fafafa",
                      fontSize: "0.9rem",
                      marginBottom: "1rem",
                    }}
                  >
                    {previewPost.first_comment}
                  </div>
                </>
              )}

              <div className="buttonRow">
                <button
                  className="publishingSubmitBtn"
                  onClick={() => copyContent(previewPost)}
                >
                  Copy Content
                </button>

                {previewPost.x_content && (
                  <button
                    className="publishingSubmitBtn"
                    onClick={() => copyXContent(previewPost)}
                  >
                    Copy X Content
                  </button>
                )}

                <button className="cancelBtn" onClick={closePreview}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
